import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { Hero } from "@/components/Hero";
import { CategoryGrid } from "@/components/CategoryGrid";
import { TrustedSellers } from "@/components/TrustedSellers";
import { ListingCard } from "@/components/ListingCard";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight, ShieldCheck, Handshake, Sprout, TrendingUp, Star } from "lucide-react";
import { SellerBadge } from "@/components/SellerBadge";
import { RstScoreBadge } from "@/components/RstScoreBadge";
import { MarketOverview } from "@/components/MarketOverview";
import { MobileSearchBar } from "@/components/MobileSearchBar";
import { AdvantagesSection, FutureModules } from "@/components/AdvantagesSection";

export default function Home() {
  const { t } = useT();
  const [categories, setCategories] = useState([]);
  const [listings, setListings] = useState([]);
  const [rising, setRising] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/categories"), api.get("/listings?sort=recent")])
      .then(([c, l]) => {
        setCategories(c.data);
        setListings(l.data.slice(0, 8));
      })
      .finally(() => setLoading(false));
    api.get("/rising-seller").then((r) => setRising(r.data)).catch(() => {});
  }, []);

  const values = [
    { Icon: ShieldCheck, title: t("home.value1Title"), text: t("home.value1Text") },
    { Icon: Handshake, title: t("home.value2Title"), text: t("home.value2Text") },
    { Icon: Sprout, title: t("home.value3Title"), text: t("home.value3Text") },
  ];

  return (
    <div>
      <Hero />

      <MobileSearchBar />

      <MarketOverview />

      {/* Derniers produits — directement sous le Hero (poulets & œufs) */}
      <section className="container-page pt-12 sm:pt-16" data-testid="latest-products">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-secondary">
              {t("home.latestTitle")}
            </h2>
            <p className="text-muted-foreground mt-2">{t("home.latestSubtitle")}</p>
          </div>
          <Link to="/catalogue" data-testid="latest-see-all"
            className="hidden sm:inline-flex items-center gap-1.5 text-primary font-semibold hover:gap-2.5 transition-all">
            {t("home.seeCatalogue")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-80 rounded-2xl" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
          </div>
        )}
      </section>

      {categories.length > 0 && <CategoryGrid categories={categories} />}

      {/* Vendeur en forte progression */}
      {rising && (
        <section className="container-page py-4" data-testid="rising-seller">
          <Link to={`/profil-vendeur/${rising.id}`}
            className="group block rounded-3xl bg-secondary text-secondary-foreground p-6 sm:p-8 overflow-hidden relative hover:shadow-xl transition-shadow">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <span className="h-16 w-16 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
                <TrendingUp className="h-9 w-9" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide bg-accent text-accent-foreground rounded-full px-3 py-1">
                  <Star className="h-3.5 w-3.5" /> {t("home.risingTitle")}
                </p>
                <h3 className="mt-2 font-heading font-extrabold text-2xl sm:text-3xl truncate">{rising.name}</h3>
                <p className="text-secondary-foreground/80 text-sm">{rising.city ? `${rising.city}, ` : ""}{rising.region || "Mali"}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <RstScoreBadge score={rising.rst_score} tier={rising.rst_tier} size="md" light />
                  <SellerBadge badge={rising.badge} premium={rising.premium} size="md" />
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-sm font-semibold">
                    <TrendingUp className="h-4 w-4" /> {t("home.risingGain", { n: rising.gained_points })}
                  </span>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 text-accent font-semibold group-hover:gap-2.5 transition-all whitespace-nowrap">
                {t("home.risingCta")} <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </Link>
        </section>
      )}

      <TrustedSellers />

      <section className="container-page pb-4 pt-16">
        <div className="grid sm:grid-cols-3 gap-6">
          {values.map(({ Icon, title, text }) => (
            <div key={title} className="rounded-2xl bg-card border border-border p-6">
              <span className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon className="h-5 w-5 text-primary" />
              </span>
              <h3 className="mt-4 font-heading font-semibold text-lg text-secondary">{title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-page py-16 sm:py-20">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-secondary">
              {t("home.popularTitle")}
            </h2>
            <p className="text-muted-foreground mt-2">{t("home.popularSubtitle")}</p>
          </div>
          <Link to="/catalogue" data-testid="see-all-listings"
            className="hidden sm:inline-flex items-center gap-1.5 text-primary font-semibold hover:gap-2.5 transition-all">
            {t("home.seeCatalogue")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-80 rounded-2xl" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
          </div>
        )}
      </section>

      <AdvantagesSection />
      <FutureModules />
    </div>
  );
}
